import { Input } from "@legal-500/legal500-design-system";
import { useFieldArray, useForm } from "react-hook-form";
import { BackgroundField, BlockItemList, useSyncBlockContent } from "./blockEditorParts";
import type { StatsContent } from "./types";
import styles from "../CMS.module.css";

type StatsEditorProps = {
  value: StatsContent;
  onChange: (value: StatsContent) => void;
};

export function StatsEditor({ value, onChange }: StatsEditorProps) {
  const { control, register, watch } = useForm<StatsContent>({ defaultValues: value });
  const { fields, append, remove, move } = useFieldArray({ control, name: "stats" });
  useSyncBlockContent(watch, value, onChange);

  return (
    <div className={styles.cardEditorFields}>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="stats-heading">
          Heading
        </label>
        <Input id="stats-heading" {...register("heading")} />
      </div>

      <BlockItemList
        noun="Stat"
        keys={fields.map(field => field.id)}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append({ value: "", label: "", tooltip: "" })}
        renderItem={(index, key) => (
          <>
            <div className={styles.cardEditorFieldRow}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`stats-value-${key}`}>
                  Value <span className={styles.required}>*</span>
                </label>
                <Input
                  id={`stats-value-${key}`}
                  required
                  pattern=".*\S.*"
                  title="Enter a value, e.g. 2,500+"
                  {...register(`stats.${index}.value`)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`stats-label-${key}`}>
                  Label <span className={styles.required}>*</span>
                </label>
                <Input
                  id={`stats-label-${key}`}
                  required
                  pattern=".*\S.*"
                  title="Enter a label"
                  {...register(`stats.${index}.label`)}
                />
              </div>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`stats-tooltip-${key}`}>
                Tooltip (optional)
              </label>
              <Input id={`stats-tooltip-${key}`} {...register(`stats.${index}.tooltip`)} />
            </div>
          </>
        )}
      />

      <BackgroundField register={register} name="background" stored={value.background} />
    </div>
  );
}
